import React, { useEffect, useMemo, useState } from "react";
import { VStack, useToast } from "@chakra-ui/react";
import { ArchiveBoxIcon } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import AudioCard from "../../components/cards/AudioCard";
import FilterBar from "../../components/cards/FilterBar";
import EmptyState from "../../components/placeholders/EmptyState";
import CustomLoader from "../../components/placeholders/CustomLoader";
import { useRecordings } from "../../context/RecordingContext";
import { Recording } from "../../model/recording";
import { extractApiErrorMessage, formatDate } from "../../helpers/utils";
import {
  CARD_SPACING,
  DEFAULT_WIDTH,
  PAGE_PADDING,
} from "../../helpers/constant";
import { listRecordings } from "../../services/recording";
import { useSharedRecordingQuery } from "../../hooks/useSharedRecordingQuery";

const monthOptions = [
  "01",
  "02",
  "03",
  "04",
  "05",
  "06",
  "07",
  "08",
  "09",
  "10",
  "11",
  "12",
];

const getMonthRange = (
  year: string,
  month: string,
): { from: number; to: number } => {
  const y = Number(year);
  const m = Number(month);
  const start = new Date(y, m - 1, 1, 0, 0, 0, 0);
  const end = new Date(y, m, 0, 23, 59, 59, 999);

  return {
    from: Math.floor(start.getTime() / 1000),
    to: Math.floor(end.getTime() / 1000),
  };
};

const ArchivePage: React.FC = () => {
  const toast = useToast();
  const { t, i18n } = useTranslation();
  const {
    isLoading,
    rubrics,
    hasInitialized,
    setHasInitialized,
    archiveState,
    setArchiveState,
    listFavoriteIDs,
    listRubrics,
    listRecordingsByRubric,
    addFavorite,
    removeFavorite,
    isFavorite,
    getRecordingFiles,
  } = useRecordings();

  const [isApplyingFilter, setIsApplyingFilter] = useState(false);
  const {
    searchQuery,
    setSearchQuery,
    sharedRecording,
    isLoadingSharedRecording,
  } = useSharedRecordingQuery({
    onLoadError: (error: unknown) => {
      toast({
        title: t("pages.archive.loadErrorTitle"),
        description: extractApiErrorMessage(
          error,
          String(t("common.tryAgain")),
        ),
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "top",
      });
    },
  });

  const {
    filterMode,
    selectedMonth,
    selectedYear,
    selectedRubricID,
    filteredRecordings,
    hasAppliedFilter,
  } = archiveState;

  const now = useMemo(() => new Date(), []);

  const yearOptions = useMemo(() => {
    const currentYear = now.getFullYear();
    const years: Array<{ value: string; label: string }> = [];

    for (let year = currentYear; year >= 2024; year -= 1) {
      const label = String(year);
      years.push({ value: label, label });
    }

    return years;
  }, [now]);

  const displayedRecordings = useMemo(() => {
    if (sharedRecording) {
      return [sharedRecording];
    }

    if (!searchQuery.trim()) {
      return filteredRecordings;
    }

    const normalized = searchQuery.trim().toLowerCase();
    return filteredRecordings.filter((recording) => {
      const searchableText = [
        recording.title,
        recording.preacherName,
        recording.categoryRubric,
        recording.category,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchableText.includes(normalized);
    });
  }, [filteredRecordings, searchQuery, sharedRecording]);

  const archiveRubrics = useMemo(
    () =>
      rubrics.filter(
        (rubric) =>
          rubric.category === "teaching" || rubric.category === "hymn",
      ),
    [rubrics],
  );

  const fetchRubricRecordings = async (
    rubricID: string,
  ): Promise<Recording[]> => {
    const availableRubrics = rubrics.length > 0 ? rubrics : await listRubrics();
    const rubric = availableRubrics.find((item) => item.rubricID === rubricID);

    if (!rubric) {
      return [];
    }

    return listRecordingsByRubric(
      rubric.category === "hymn" ? "exhortation" : "teaching",
      rubricID,
    );
  };

  useEffect(() => {
    if (filterMode !== "categoryOrRubric") {
      return;
    }

    if (selectedRubricID || archiveRubrics.length > 0) {
      if (!selectedRubricID && archiveRubrics[0]) {
        setArchiveState((prev) => ({
          ...prev,
          selectedRubricID: archiveRubrics[0].rubricID,
        }));
      }
      return;
    }

    listRubrics()
      .then((fetchedRubrics) => {
        const filteredRubrics = fetchedRubrics.filter(
          (rubric) =>
            rubric.category === "teaching" || rubric.category === "hymn",
        );

        if (filteredRubrics[0]) {
          setArchiveState((prev) => ({
            ...prev,
            selectedRubricID: filteredRubrics[0].rubricID,
          }));
        }
      })
      .catch(() => undefined);
  }, [
    archiveRubrics,
    filterMode,
    listRubrics,
    selectedRubricID,
    setArchiveState,
  ]);

  useEffect(() => {
    if (hasInitialized.archive) {
      return;
    }

    let isMounted = true;

    const initialize = async () => {
      try {
        setIsApplyingFilter(true);
        await listFavoriteIDs();
        const result = await listRecordings(
          getMonthRange(selectedYear, selectedMonth),
        );

        if (isMounted) {
          setArchiveState((prev) => ({
            ...prev,
            filteredRecordings: result.recordings,
            hasAppliedFilter: true,
          }));
        }
      } catch (error) {
        toast({
          title: t("pages.archive.loadErrorTitle"),
          description: extractApiErrorMessage(
            error,
            String(t("common.tryAgain")),
          ),
          status: "error",
          duration: 4000,
          isClosable: true,
          position: "top",
        });
      } finally {
        if (isMounted) {
          setHasInitialized((prev) => ({ ...prev, archive: true }));
          setIsApplyingFilter(false);
        }
      }
    };

    initialize();

    return () => {
      isMounted = false;
    };
  }, [
    hasInitialized.archive,
    listFavoriteIDs,
    selectedMonth,
    selectedYear,
    setArchiveState,
    setHasInitialized,
    t,
    toast,
  ]);

  const handleApplyFilter = async () => {
    try {
      setIsApplyingFilter(true);
      await listFavoriteIDs();

      const result =
        filterMode === "date"
          ? await listRecordings(
              getMonthRange(selectedYear, selectedMonth),
            ).then((response) => response.recordings)
          : await fetchRubricRecordings(selectedRubricID);

      setArchiveState((prev) => ({
        ...prev,
        filteredRecordings: result,
        hasAppliedFilter: true,
      }));
    } catch (error) {
      toast({
        title: t("pages.archive.filterErrorTitle"),
        description: extractApiErrorMessage(
          error,
          String(t("common.tryAgain")),
        ),
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setIsApplyingFilter(false);
    }
  };

  const hasNoData = hasAppliedFilter && filteredRecordings.length === 0;
  const hasNoFilteredResults =
    hasAppliedFilter && displayedRecordings.length === 0 && !hasNoData;

  return (
    <VStack w={DEFAULT_WIDTH} px={PAGE_PADDING} mx="auto" align="start">
      <FilterBar
        variant="archive"
        onSearch={setSearchQuery}
        searchValue={searchQuery}
        placeholder={String(t("pages.archive.searchPlaceholder"))}
        filterMode={filterMode}
        onFilterModeChange={(mode) => {
          setArchiveState((prev) => ({
            ...prev,
            filterMode: mode,
            hasAppliedFilter: false,
            filteredRecordings: [],
          }));
        }}
        selectedMonth={selectedMonth}
        onMonthChange={(month) => {
          setArchiveState((prev) => ({ ...prev, selectedMonth: month }));
        }}
        selectedYear={selectedYear}
        onYearChange={(year) => {
          setArchiveState((prev) => ({ ...prev, selectedYear: year }));
        }}
        rubrics={archiveRubrics}
        selectedArchiveRubricID={selectedRubricID}
        onArchiveRubricChange={(rubricID) => {
          setArchiveState((prev) => ({ ...prev, selectedRubricID: rubricID }));
        }}
        monthOptions={monthOptions.map((month) => ({
          value: month,
          label: t(`filters.months.${month}`),
        }))}
        yearOptions={yearOptions}
        onApplyFilter={handleApplyFilter}
        isLoading={isLoading || isApplyingFilter}
      />

      {isLoading ||
      isApplyingFilter ||
      isLoadingSharedRecording ||
      !hasInitialized.archive ? (
        <CustomLoader />
      ) : !hasAppliedFilter ? (
        <EmptyState
          icon={ArchiveBoxIcon}
          title={t("pages.archive.defaultTitle")}
          description={t("pages.archive.defaultDescription")}
        />
      ) : hasNoData ? (
        <EmptyState
          icon={ArchiveBoxIcon}
          title={t("pages.archive.emptyTitle")}
          description={t("pages.archive.emptyDescription")}
        />
      ) : hasNoFilteredResults ? (
        <EmptyState
          icon={ArchiveBoxIcon}
          title={t("pages.search.noResultsTitle")}
          description={t("pages.search.noResultsDescription")}
        />
      ) : (
        <VStack spacing={CARD_SPACING} w="full">
          {displayedRecordings.map((recording) => (
            <AudioCard
              key={recording.recordingID}
              recordingID={recording.recordingID}
              title={recording.title}
              preacherName={recording.preacherName}
              date={formatDate(
                recording.date,
                i18n.language.startsWith("fr") ? "fr" : "en",
              )}
              hasRecording={Boolean(recording.recordingS3Key)}
              hasSummary={Boolean(recording.summaryS3Key)}
              youtubeLink={recording.youtubeLink}
              saved={isFavorite(recording.recordingID)}
              onAddFavorite={() =>
                addFavorite(recording.recordingID, recording)
              }
              onRemoveFavorite={removeFavorite}
              onRequestFiles={(recordingID) => getRecordingFiles(recordingID)}
            />
          ))}
        </VStack>
      )}
    </VStack>
  );
};

export default ArchivePage;
